/**
 * Avatar utility - generates consistent avatars without external dependencies
 */

// Color palette for avatars (matching app design)
const AVATAR_COLORS = [
  { bg: '#D97757', text: '#FFFFFF' }, // Primary orange
  { bg: '#8B5CF6', text: '#FFFFFF' }, // Purple
  { bg: '#EC4899', text: '#FFFFFF' }, // Pink
  { bg: '#10B981', text: '#FFFFFF' }, // Green
  { bg: '#3B82F6', text: '#FFFFFF' }, // Blue
  { bg: '#F59E0B', text: '#FFFFFF' }, // Amber
  { bg: '#EF4444', text: '#FFFFFF' }, // Red
  { bg: '#14B8A6', text: '#FFFFFF' }, // Teal
];

/**
 * Generate initials from a name
 * @param {string} name - Full name
 * @returns {string} - Up to 2 character initials
 */
export const getInitials = (name) => {
  if (!name) return '?';
  
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

/**
 * Get a consistent color based on name
 * @param {string} name - Name to hash
 * @returns {object} - { bg, text } color object
 */
export const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  
  const hash = name.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

/**
 * Generate an SVG avatar data URL
 * @param {string} name - Name to generate avatar for
 * @param {number} size - Size in pixels (default 100)
 * @returns {string} - Data URL for the SVG
 */
export const generateAvatarUrl = (name, size = 100) => {
  const initials = getInitials(name);
  const colors = getAvatarColor(name);
  
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <rect width="${size}" height="${size}" fill="${colors.bg}" rx="${size / 2}"/>
      <text 
        x="50%" 
        y="50%" 
        dominant-baseline="central" 
        text-anchor="middle" 
        fill="${colors.text}" 
        font-family="system-ui, -apple-system, sans-serif" 
        font-weight="600" 
        font-size="${size * 0.4}"
      >${initials}</text>
    </svg>
  `.trim();
  
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * React component style avatar generator
 * Returns inline styles and content for a div-based avatar
 */
export const getAvatarStyles = (name, size = 40) => {
  const initials = getInitials(name);
  const colors = getAvatarColor(name);
  
  return {
    style: {
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: colors.bg,
      color: colors.text,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 600,
      fontSize: size * 0.4,
      fontFamily: 'system-ui, -apple-system, sans-serif',
    },
    initials,
  };
};
